import { h, defineComponent, ref, watch } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { box, indexView, loader, pagination } from '@/ui/components';
import { bulkDelete, list as getGroupList, remove } from '@/services/api/resources/group';
import { emptyList } from '@/services/api';
import { GroupList, GroupRecord } from '@/entities/group';
import { usePagination, useSorting, useTask } from '@/ui/concerns';
import { bulkDeleteButton } from '@/ui/components/Button';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import { dataNameProp } from '@/ui/helpers/uiHelper';
import { tagColumn } from '@/ui/components/table/TableColumn';
import { renderForbiddenPage } from '../error/ForbiddenPage';
import * as qs from '@/ui/queryState';
import { searchFilter } from '@/ui/components/filters/filters-horizontal';
import { filterFormHorizontal } from '@/ui/components/Columns';
import { SortDirection } from '@/ui/types';
import { useGroupCheck } from '@/ui/pages/group/useGroupCheck';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
import GroupEmpty from '@/ui/pages/group/GroupEmpty.vue';
import { BulkDeletableTable } from '@/ui/components/table/BulkDeletableTable';
import { useBulkDeletable } from '@/ui/pages/useBulkDeletable';

export const GroupIndexPage = defineComponent({
  name: 'GroupIndexPage',
  setup() {
    if (!hasFeature('groups')) {
      return renderResourceTeaserPage(t('common.groups'));
    }

    if (!hasPermission(Permissions.MANAGE_USERS)) {
      return renderForbiddenPage;
    }

    const initialLoading = ref(true);
    const search = qs.string('search', '');

    // state stored in query string
    const { page, perPage } = usePagination();
    const { sort, dir } = useSorting('createdAt', SortDirection.Descending);
    const { checkResult, performCheck } = useGroupCheck();

    // get list task
    const list = ref<GroupList>(emptyList());
    const getList = useTask(
      () => getGroupList({
        page: page.value,
        perPage: perPage.value,
        search: search.value,
        sort: sort.value,
        dir: dir.value,
      }),
      listResponse => {
        list.value = listResponse;
        initialLoading.value = false;
      },
    );
    watch([page, perPage, search], getList.run);

    const { selectedResources, deleteBulkTask, onCheckedRowsUpdate, isBulkDeletionDisabled } = useBulkDeletable({
      getList,
      bulkDeleteFn: bulkDelete,
    });

    return function render() {
      return loader('div', performCheck, [
        checkResult.value ? indexView(
          [
            filterFormHorizontal([
              bulkDeleteButton({ resources: selectedResources, disabled: isBulkDeletionDisabled }),
              searchFilter(search),
            ]),
          ],
          [
            box([
              loader('div', getList, [
                h(BulkDeletableTable, {
                  props: {
                    editRoute: 'groupEdit',
                    list: list.value.data,
                    deleteFn: remove,
                    selectedRecords: selectedResources.value,
                    loading: deleteBulkTask.state.value.pending,
                    paginationLinks: list.value.meta,
                    isRowCheckable: (row: BaseEntity) => row.authorizedTo.delete,
                    recordName: {
                      plural: t('common.groups'),
                      singular: t('common.group'),
                    },
                  },
                  on: {
                    'update:checkedRows': onCheckedRowsUpdate,
                  },
                  scopedSlots: {
                    title: ({ row: group }: { row: GroupRecord }) => group.data.title,
                    default: () => [
                      tagColumn({
                        field: 'users',
                        label: t('common.users'),
                      }, {
                        default: ({ row: group }: { row: GroupRecord }) =>
                          group.data.users.map(dataNameProp).join(', '),
                      }),
                    ],
                  },
                }), pagination({
                  current: page.value,
                  total: list.value.meta.total,
                  perPage: perPage.value,
                }),
              ], !initialLoading.value),
            ]),
          ],
        ) : box([h(GroupEmpty)]),
      ], false);
    };
  },
});
